import { forwardRef } from "react";
import { useForwardedOrLocalRef, useUniqueIds } from "../../../shared/hooks";
import { ClearButton } from "./components";
import type { BaseProps } from "./shared";
import styles from "./InsetLabelField.module.css";

export const Sizes = [
  // As of August 2025, DDS Design System doesn't support small size for the insetLabel variant. However,
  // it's included here so that it has the same type signature as the default variant. See
  // https://github.com/classdojo/web-monorepo/pull/12514 for more details.
  "small",
  "medium",
  "large",
] as const;
export const Variant = "insetLabel";

export interface Props extends BaseProps {
  /**
   * Docs:
   * - {@link https://www.figma.com/design/erhFTAWNMugPVrJGCAWsse/%E2%9D%96-Dojo-Design-System-%E2%80%93-DDS?node-id=781-11468 Figma}
   * - {@link https://components.internal.classdojo.com/?path=/story/components-ddstextfield--inset-label-variant Storybook}
   */
  variant: typeof Variant;
  size?: (typeof Sizes)[number];
  ariaLabel?: string;
  /**
   * Not available for insetLabel variant (the label is the placeholder)
   */
  placeholder?: never;
  autoFocus?: boolean;
  /**
   * Content to display on the left side of the input field
   */
  leftSlot?: React.ReactNode;
}

export const InsetLabelField = forwardRef<HTMLInputElement, Props>(
  (
    {
      ["data-name"]: dataName,
      disabled,
      id,
      label,
      ariaLabel,
      size = "medium",
      error,
      hint,
      clearButton,
      rightSlot,
      leftSlot,
      onChange,
      "aria-describedby": ariaDescribedByFromUser,
      ...rest
    },
    forwardedRef,
  ) => {
    const ref = useForwardedOrLocalRef(forwardedRef);
    const [labelId, inputId, hintId] = useUniqueIds(["DDSTextField.label", "DDSTextField.input", "DDSTextField.hint"]);
    const ariaDescribedByMerged =
      [hint ? hintId : undefined, ariaDescribedByFromUser].filter(Boolean).join(" ").trim() || undefined;
    const className = [styles.textField, styles[size], error ? styles.error : null].join(" ");

    const renderRightSlot = () => {
      if (clearButton && rightSlot) {
        return (
          <>
            <div className={styles.rightSlot}>{rightSlot}</div>
            <ClearButton className={styles.clearButton} forwardedRef={ref} />
          </>
        );
      }

      if (clearButton) {
        return <ClearButton className={styles.clearButton} forwardedRef={ref} />;
      }

      if (rightSlot) {
        return rightSlot;
      }

      return null;
    };

    return (
      <div className={className}>
        <div className={styles.inputBox}>
          {leftSlot && <div className={styles.leftSlot}>{leftSlot}</div>}
          <label id={labelId} htmlFor={id ?? inputId} className={styles.label}>
            {label}
          </label>
          <input
            aria-label={ariaLabel}
            className={styles.input}
            data-name={dataName}
            disabled={disabled}
            id={id ?? inputId}
            ref={ref}
            aria-describedby={ariaDescribedByMerged}
            aria-invalid={error}
            /* insert empty space to make it easier to detect an empty input in the css (more details there) */
            placeholder=" "
            onChange={(e) => onChange?.(e.target.value, e)}
            {...rest}
          />
          {renderRightSlot()}
        </div>
        {hint && (
          <span
            role={error ? "alert" : undefined}
            aria-live={!error ? "polite" : undefined}
            id={hintId}
            className={styles.hint}
          >
            {hint}
          </span>
        )}
      </div>
    );
  },
);
